import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportCategory } from '../../../models/Support/ISupportCategory';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import {
	type SupportAgentTicketCountMap,
	UNASSIGNED_COUNT_KEY,
} from '../../../queries/Support/useSupportAgentTicketCounts';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import {
	useAssignTicketMutation,
	useSetTicketMailboxMutation,
	useUpdateTicketCategoryMutation,
	useUpdateTicketPriorityMutation,
	useUpdateTicketStatusMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { CategoryPicker } from './CategoryPicker';
import { agentTicketCountHint } from './agentTicketCountHint';
import { inboxStyles as s } from './styles/inboxStyles';
import { getStatusInfo } from './supportTicketPresentation';
import { ticketMutationFeedback as feedback } from './ticketMutationFeedback';
import { PRIORITY_EDIT_OPTIONS, STATUS_EDIT_OPTIONS } from './TicketTableActions';
import type { TicketFieldShortcut } from './useTicketShortcuts';

interface TicketDetailsEditorProps {
	ticket: ISupportTicket;
	categories: ISupportCategory[];
	teamMembers: { id: string; name: string }[];
	/** Open-ticket load per agent, shown beside each name in the assignee picker. */
	agentTicketCounts?: SupportAgentTicketCountMap;
	/** Called after the ticket leaves the agent's plate (closed, spam, archived, or assigned) — parent advances. */
	onTicketLeftView?: () => void;
	/** Which field the `a` / `s` shortcuts have raised, if any; cleared through {@link onShortcutFieldChange}. */
	shortcutField?: TicketFieldShortcut | null;
	onShortcutFieldChange?: (field: TicketFieldShortcut | null) => void;
}

/**
 * Editable Status / Priority / Assignee / Category / Mailbox controls for the details panel. These moved
 * here from the ticket top bar: the masthead app bar is now a read-only identity strip, and all ticket
 * mutations live alongside the rest of the ticket metadata in the right-hand details pane.
 */
export const TicketDetailsEditor: React.FC<TicketDetailsEditorProps> = ({
	ticket,
	categories,
	teamMembers,
	agentTicketCounts,
	onTicketLeftView,
	shortcutField,
	onShortcutFieldChange,
}) => {
	const [showSpamConfirm, setShowSpamConfirm] = React.useState(false);
	const [pendingMailboxId, setPendingMailboxId] = React.useState<string | null>(null);

	const updateStatus = useUpdateTicketStatusMutation(ticket.id);
	const assignTicket = useAssignTicketMutation(ticket.id);
	const updatePriority = useUpdateTicketPriorityMutation(ticket.id);
	const updateCategory = useUpdateTicketCategoryMutation(ticket.id);
	const setMailbox = useSetTicketMailboxMutation(ticket.id);

	// The caller's reachable desks — not a guaranteed destination: the API also requires agent status, and
	// refuses a move out of a restricted desk unless the caller is an agent of it. Inactive desks are
	// rejected server-side, so they never become an option here.
	const { data: mailboxes } = useSupportMailboxListQuery();
	const reachableMailboxes = mailboxes ?? [];
	const moveTargets = reachableMailboxes.filter(mailbox => mailbox.isActive);
	const pendingMailbox = moveTargets.find(mailbox => mailbox.id === pendingMailboxId);
	const shortcutSelectProps = (field: TicketFieldShortcut) =>
		onShortcutFieldChange
			? { open: shortcutField === field, onOpenChange: (next: boolean) => onShortcutFieldChange(next ? field : null) }
			: {};

	const applyStatus = (status: TicketStatus) =>
		updateStatus.mutate(
			status,
			feedback(
				'Failed to update ticket status.',
				status === TicketStatus.Closed || status === TicketStatus.Spam || status === TicketStatus.Archive
					? onTicketLeftView
					: undefined
			)
		);

	return (
		<>
			<div className={css(s.detailEditRows)}>
				<div className={css(s.detailEditRow)}>
					<span className={css(s.detailKey)}>Status</span>
					<Select
						value={ticket.status}
						{...shortcutSelectProps('status')}
						onValueChange={value => {
							const option = STATUS_EDIT_OPTIONS.find(item => item.value === value);
							if (!option) return;
							// Spam drops the ticket out of every working view and off the agent's plate, and it sits one
							// row below Closed in the menu — confirm before acting on what may be a misclick.
							if (option.value === TicketStatus.Spam) {
								setShowSpamConfirm(true);
								return;
							}
							applyStatus(option.value);
						}}
					>
						<SelectTrigger aria-label='Ticket status'>
							<SelectValue render={() => <span data-slot='select-value'>{getStatusInfo(ticket.status).label}</span>} />
						</SelectTrigger>
						<SelectContent>
							{STATUS_EDIT_OPTIONS.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className={css(s.detailEditRow)}>
					<span className={css(s.detailKey)}>Priority</span>
					<Select
						value={ticket.priority || TicketPriority.Normal}
						onValueChange={value => {
							const option = PRIORITY_EDIT_OPTIONS.find(item => item.value === value);
							if (option) updatePriority.mutate(option.value, feedback('Failed to update priority.'));
						}}
					>
						<SelectTrigger aria-label='Ticket priority'>
							<SelectValue
								render={() => <span data-slot='select-value'>{ticket.priority || TicketPriority.Normal}</span>}
							/>
						</SelectTrigger>
						<SelectContent>
							{PRIORITY_EDIT_OPTIONS.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className={css(s.detailEditRow)}>
					<span className={css(s.detailKey)}>Assignee</span>
					<Select
						value={ticket.assigneeId || ''}
						{...shortcutSelectProps('assign')}
						onValueChange={value =>
							assignTicket.mutate(
								value || null,
								feedback('Failed to assign ticket.', value ? onTicketLeftView : undefined)
							)
						}
					>
						<SelectTrigger aria-label='Ticket assignee'>
							<SelectValue
								placeholder='Unassigned'
								render={() => {
									const match = teamMembers.find(a => a.id === ticket.assigneeId);
									return (
										<span data-slot='select-value'>
											{match?.name ?? (ticket.assigneeId ? ticket.assigneeId : 'Unassigned')}
										</span>
									);
								}}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectItem
								key='assignee-unassigned'
								value=''
								hint={agentTicketCountHint(agentTicketCounts, UNASSIGNED_COUNT_KEY)}
							>
								Unassigned
							</SelectItem>
							{teamMembers.map(agent => (
								<SelectItem key={agent.id} value={agent.id} hint={agentTicketCountHint(agentTicketCounts, agent.id)}>
									{agent.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className={css(s.detailEditRow)}>
					<span className={css(s.detailKey)}>Category</span>
					<CategoryPicker
						categories={categories}
						selectedId={ticket.categoryId}
						onSelect={id => updateCategory.mutate(id || null, feedback('Failed to update category.'))}
					/>
				</div>

				{moveTargets.some(mailbox => mailbox.id !== ticket.mailboxId) && (
					<div className={css(s.detailEditRow)}>
						<span className={css(s.detailKey)}>Mailbox</span>
						<Select
							value={ticket.mailboxId ?? ''}
							disabled={setMailbox.isPending}
							onValueChange={value => {
								if (!value || value === ticket.mailboxId) return;
								setPendingMailboxId(value);
							}}
						>
							<SelectTrigger aria-label='Ticket mailbox'>
								<SelectValue
									render={() => {
										// Not moveTargets: a ticket on a deactivated desk has no match there, and its raw id
										// must never stand in for the desk name.
										const current = reachableMailboxes.find(mailbox => mailbox.id === ticket.mailboxId);
										return <span data-slot='select-value'>{current?.name ?? 'Unknown mailbox'}</span>;
									}}
								/>
							</SelectTrigger>
							<SelectContent>
								{moveTargets.map(mailbox => (
									<SelectItem key={mailbox.id} value={mailbox.id}>
										{mailbox.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			</div>
			{pendingMailbox && (
				<ConfirmModal
					title={`Move to ${pendingMailbox.name}`}
					message={`This ticket will leave this mailbox and its assignee and category will be cleared, since both belong to the mailbox it is on. It will also be renumbered into ${pendingMailbox.name}'s sequence, so #${ticket.number} will no longer find it.`}
					confirmLabel='Move ticket'
					onConfirm={() => {
						const destinationId = pendingMailbox.id;
						setPendingMailboxId(null);
						setMailbox.mutate(destinationId, feedback('Failed to move ticket.', onTicketLeftView));
					}}
					onCancel={() => setPendingMailboxId(null)}
				/>
			)}
			{showSpamConfirm && (
				<ConfirmModal
					title='Mark as Spam'
					message='Are you sure you want to mark this ticket as spam? The ticket will be moved to the Spam folder.'
					confirmLabel='Mark as Spam'
					variant='danger'
					onConfirm={() => {
						setShowSpamConfirm(false);
						applyStatus(TicketStatus.Spam);
					}}
					onCancel={() => setShowSpamConfirm(false)}
				/>
			)}
		</>
	);
};
